import React, { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import PageName from "../../components/PageName";
import Card from "../../components/Base/Card";
import ErrorViewer from "../../components/ErrorViewer";
import LoadingSpinner from "../../components/LoadingSpinner";
import SuccessModal from "../../components/SuccessModal";
import EmployerSelect from "../../components/EmployerSelect";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import { getCountries, getLGA, getStates } from "../../redux/utils/utilsSlice";
import {
  GetAllEmployerBranch,
  GetRelationship,
} from "../../redux/selfService/selfServiceSlice";
import selfServicesService from "../../redux/selfService/selfServicesService";
import { RouteNames } from "../../constants";
import { EmployerSelectValue } from "../../interfaces";
import InactivityTimeoutHOC from "../auth/inactivity-timeout-hoc";

const TITLE_OPTIONS = [
  { value: 1, label: "Miss" },
  { value: 2, label: "Mr" },
  { value: 3, label: "Mrs" },
  { value: 4, label: "Ms" },
];

const GENDER_OPTIONS = [
  { value: 1, label: "Male" },
  { value: 2, label: "Female" },
];

const MARITAL_OPTIONS = [
  { value: 1, label: "Separated" },
  { value: 2, label: "Widow" },
  { value: 3, label: "Divorce" },
  { value: 4, label: "Married" },
  { value: 5, label: "Single" },
];

const NATIONALITY_OPTIONS = [
  { value: "1", label: "Nigerian" },
  { value: "0", label: "Non-Nigerian" },
];

const validationSchema = Yup.object().shape({
  bvn: Yup.string().required("BVN is required").max(15),
  nin: Yup.string().required("NIN is required").min(11).max(15),
  title: Yup.number().required("Title is required"),
  surname: Yup.string().required("Surname is required"),
  firstName: Yup.string().required("First name is required"),
  middleName: Yup.string(),
  maidenOrFormerName: Yup.string(),
  maritalStatusId: Yup.number().required("Marital status is required"),
  nationality: Yup.string().required("Nationality is required"),
  lgaOfOriginCodeId: Yup.number().nullable(),
  dateOfBirth: Yup.string().required("Date of birth is required"),
  placeOfBirth: Yup.string().required("Place of birth is required"),
  email: Yup.string().email("Invalid email").nullable(),
  phoneNumber: Yup.string().required("Phone number is required"),
  residenceCountryCode: Yup.string().required("Residence country is required"),
  residenceStateCode: Yup.string().required("Residence state is required"),
  residenceLocalGovernmentId: Yup.number().required("LGA is required"),
  residenceTownCity: Yup.string().required("Town/City is required"),
  residenceStreetName: Yup.string().required("Street is required"),
  residenceHouseNameOrNumber: Yup.string().required("House number is required"),
  residenceZipCode: Yup.string().nullable(),
  employerBranchId: Yup.number().required("Employer branch is required"),
  natureOfBusiness: Yup.string().nullable(),
  dateOfFirstAppointment: Yup.string().nullable(),
  dateOfCurrentEmployment: Yup.string().nullable(),
  employmentStateCode: Yup.string().nullable(),
  employmentTownCity: Yup.string().nullable(),
  employmentStreetName: Yup.string().nullable(),
  employmentBuildingNameOrNumber: Yup.string().nullable(),
  employmentLocalGovernmentId: Yup.number().nullable(),
  nokTitle: Yup.number().required("NOK title is required"),
  nokGender: Yup.number().required("NOK gender is required"),
  nokSurname: Yup.string().required("NOK surname is required"),
  nokFirstName: Yup.string().required("NOK first name is required"),
  nokMiddleName: Yup.string().nullable(),
  relationshipId: Yup.number().required("Relationship is required"),
  nokResidenceCountryId: Yup.number().required("NOK country is required"),
  nokPhoneNumber: Yup.string().required("NOK phone is required"),
  nokEmail: Yup.string().email("Invalid email").nullable(),
  nokResidenceStreetName: Yup.string().nullable(),
  nokResidenceHouseNumber: Yup.string().nullable(),
  nokResidenceTownCity: Yup.string().nullable(),
  nokResidenceStateCode: Yup.string().nullable(),
});

const draftValidationSchema = Yup.object().shape({
  surname: Yup.string().required("Surname is required to save a draft"),
  firstName: Yup.string().required("First name is required to save a draft"),
  employerBranchId: Yup.number().nullable(),
});

const toDateInput = (value?: string | null) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  return d.toISOString().slice(0, 10);
};

const normalizeNationality = (value?: string | null) => {
  if (value == null || value === "") return "1";
  const v = String(value).trim();
  if (
    v === "1" ||
    v.toUpperCase() === "NG" ||
    v.toLowerCase() === "nigerian" ||
    v.toUpperCase() === "N" ||
    v.toLowerCase() === "true"
  ) {
    return "1";
  }
  return "0";
};

const RecapturePage = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { user } = useAppSelector((state) => state.auth);
  const { allStates, lgas, allCountries } = useAppSelector((state) => state.utils);
  const { relationships, allEmployerBranch, isLoading: branchLoading } =
    useAppSelector((state) => state.selfService);

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [savingDraft, setSavingDraft] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [canEdit, setCanEdit] = useState(true);
  const [isDraft, setIsDraft] = useState(false);
  const [statusMessage, setStatusMessage] = useState("");
  const [statusDisplay, setStatusDisplay] = useState("");
  const [picturePreview, setPicturePreview] = useState<string | null>(null);
  const [signaturePreview, setSignaturePreview] = useState<string | null>(null);
  const [consentPreview, setConsentPreview] = useState<string | null>(null);
  const [consentIsPdf, setConsentIsPdf] = useState(false);
  const [employerDefault, setEmployerDefault] = useState<EmployerSelectValue | null>(null);
  const [pictureFile, setPictureFile] = useState<File | null>(null);
  const [signatureFile, setSignatureFile] = useState<File | null>(null);
  const [consentFile, setConsentFile] = useState<File | null>(null);
  const [hasExistingPicture, setHasExistingPicture] = useState(false);
  const [hasExistingSignature, setHasExistingSignature] = useState(false);
  const [hasExistingConsent, setHasExistingConsent] = useState(false);
  const [faceValidating, setFaceValidating] = useState(false);
  const [faceCompliant, setFaceCompliant] = useState<boolean | null>(null);
  const [faceStatusMessage, setFaceStatusMessage] = useState("");

  const {
    register,
    handleSubmit,
    reset,
    setValue,
    getValues,
    watch,
    trigger,
    formState: { errors },
  } = useForm({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      nationality: "1",
      residenceCountryCode: "NG",
    },
  });

  const residenceStateCode = watch("residenceStateCode");
  const employerBranchId = watch("employerBranchId");

  useEffect(() => {
    if (user?.isRecaptured) return;
    const load = async () => {
      setLoading(true);
      try {
        await Promise.all([
          dispatch(getStates()),
          dispatch(getCountries()),
          dispatch(GetRelationship()),
        ]);
        const response = await selfServicesService.getRecaptureDetails();
        const data = response?.data?.responseObject;
        if (!data) {
          toast.error(response?.data?.statusMessage || "Unable to load recapture details.");
          return;
        }

        const editable = data.canEdit ?? data.canSubmit ?? true;
        setCanEdit(!!editable);
        setIsDraft(!!data.isDraft);
        setStatusMessage(data.statusMessage || "");
        setStatusDisplay(data.statusDisplay || data.recaptureStatus || "");
        setPicturePreview(data.picturePreview || null);
        setHasExistingPicture(!!data.hasPicture);
        setHasExistingSignature(!!data.hasSignature);
        setHasExistingConsent(!!data.hasConsentForm);

        if (data.signaturePreview) {
          setSignaturePreview(
            data.signaturePreview.startsWith("data:")
              ? data.signaturePreview
              : `data:image/jpeg;base64,${data.signaturePreview}`
          );
        } else {
          setSignaturePreview(null);
        }

        if (data.consentPreview) {
          const isPdf = !!data.consentIsPdf;
          setConsentIsPdf(isPdf);
          setConsentPreview(
            data.consentPreview.startsWith("data:")
              ? data.consentPreview
              : isPdf
                ? `data:application/pdf;base64,${data.consentPreview}`
                : `data:image/jpeg;base64,${data.consentPreview}`
          );
        } else {
          setConsentPreview(null);
          setConsentIsPdf(false);
        }

        if (!editable && !data.isDraft) {
          // Pending / locked — send user to status page
          navigate(RouteNames.RECAPTURE_STATUS, { replace: true });
          return;
        }

        const p = data.personalData || {};
        const e = data.employmentRecord || {};
        const n = data.nextOfKinDetail || {};

        if (p.residenceStateCode) {
          await dispatch(getLGA({ stateCode: p.residenceStateCode }));
        }

        if (e.employerId) {
          await dispatch(GetAllEmployerBranch({ employerId: e.employerId }));
          if (e.employerName) {
            setEmployerDefault({
              label: e.employerName,
              value: {
                id: e.employerId,
                txtemployername: e.employerName,
              } as any,
            });
          }
        }

        reset({
          bvn: p.bvn || "",
          nin: p.nin || "",
          title: p.title || undefined,
          surname: p.surname || "",
          firstName: p.firstName || "",
          middleName: p.middleName || "",
          maidenOrFormerName: p.maidenOrFormerName || "",
          maritalStatusId: p.maritalStatusId || undefined,
          nationality: normalizeNationality(p.nationality),
          lgaOfOriginCodeId: p.lgaOfOriginCodeId || undefined,
          dateOfBirth: toDateInput(p.dateOfBirth),
          placeOfBirth: p.placeOfBirth || "",
          email: p.email || "",
          phoneNumber: p.phoneNumber || "",
          residenceCountryCode: p.residenceCountryCode || "NG",
          residenceStateCode: p.residenceStateCode || "",
          residenceLocalGovernmentId: p.residenceLocalGovernmentId || undefined,
          residenceTownCity: p.residenceTownCity || "",
          residenceStreetName: p.residenceStreetName || "",
          residenceHouseNameOrNumber: p.residenceHouseNameOrNumber || "",
          residenceZipCode: p.residenceZipCode || "",
          employerBranchId: e.employerBranchId || undefined,
          natureOfBusiness: e.natureOfBusiness || "",
          dateOfFirstAppointment: toDateInput(e.dateOfFirstAppointment),
          dateOfCurrentEmployment: toDateInput(e.dateOfCurrentEmployment),
          employmentStateCode: e.stateCode || "",
          employmentTownCity: e.townCity || "",
          employmentStreetName: e.streetName || "",
          employmentBuildingNameOrNumber: e.buildingNameOrNumber || "",
          employmentLocalGovernmentId: e.localGovernmentId || undefined,
          nokTitle: n.title || undefined,
          nokGender: n.gender || undefined,
          nokSurname: n.surname || "",
          nokFirstName: n.firstName || "",
          nokMiddleName: n.middleName || "",
          relationshipId: n.relationshipId || undefined,
          nokResidenceCountryId: n.residenceCountryId || undefined,
          nokPhoneNumber: n.phoneNumber || "",
          nokEmail: n.email || "",
          nokResidenceStreetName: n.residenceStreetName || "",
          nokResidenceHouseNumber: n.residenceHouseNumber || "",
          nokResidenceTownCity: n.residenceTownCity || "",
          nokResidenceStateCode: n.residenceStateCode || "",
        });
      } catch (err: any) {
        toast.error(err?.response?.data?.statusMessage || "Unable to load recapture details.");
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [dispatch, reset, user?.isRecaptured, navigate]);

  useEffect(() => {
    if (residenceStateCode) {
      dispatch(getLGA({ stateCode: residenceStateCode }));
    }
  }, [dispatch, residenceStateCode]);

  const branchOptions = useMemo(
    () =>
      (allEmployerBranch || []).map((b: any) => ({
        value: b.id,
        label: b.txtEmployerBranchName || b.txtemployerbranchname || b.name || `Branch ${b.id}`,
      })),
    [allEmployerBranch]
  );

  const onEmployerSelected = (selected: EmployerSelectValue | null) => {
    setEmployerDefault(selected);
    const employerId = (selected?.value as any)?.id;
    setValue("employerBranchId", undefined as any, { shouldValidate: true });
    if (employerId) {
      dispatch(GetAllEmployerBranch({ employerId }));
    }
  };

  const revokeUrl = (url: string | null) => {
    if (url && url.startsWith("blob:")) URL.revokeObjectURL(url);
  };

  const onPictureSelected = async (file: File | null) => {
    revokeUrl(picturePreview?.startsWith("blob:") ? picturePreview : null);
    setPictureFile(file);
    setFaceCompliant(null);
    setFaceStatusMessage("");
    if (!file) {
      setPicturePreview(null);
      return;
    }
    const localUrl = URL.createObjectURL(file);
    setPicturePreview(localUrl);
    setFaceValidating(true);
    try {
      const response = await selfServicesService.validateRecapturePassport(file);
      const compliant = response?.data?.responseObject === true;
      const message =
        response?.data?.statusMessage ||
        (compliant ? "Face is Compliant" : "Face is Non-Compliant, please retake passport.");
      setFaceCompliant(compliant);
      setFaceStatusMessage(message);
      if (!compliant) {
        toast.error(message);
      } else {
        toast.success(message);
      }
    } catch (err: any) {
      const message =
        err?.response?.data?.statusMessage ||
        "Unable to validate passport photograph. Please try another image.";
      setFaceCompliant(false);
      setFaceStatusMessage(message);
      toast.error(message);
    } finally {
      setFaceValidating(false);
    }
  };

  const onSignatureSelected = (file: File | null) => {
    revokeUrl(signaturePreview?.startsWith("blob:") ? signaturePreview : null);
    setSignatureFile(file);
    setSignaturePreview(file ? URL.createObjectURL(file) : null);
  };

  const onConsentSelected = (file: File | null) => {
    revokeUrl(consentPreview?.startsWith("blob:") ? consentPreview : null);
    setConsentFile(file);
    if (!file) {
      setConsentPreview(null);
      setConsentIsPdf(false);
      return;
    }
    const isPdf =
      file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf");
    setConsentIsPdf(isPdf);
    setConsentPreview(URL.createObjectURL(file));
  };

  const append = (form: FormData, key: string, value: any) => {
    if (value === undefined || value === null || value === "") return;
    form.append(key, String(value));
  };

  const buildFormData = (values: any, includeDocs: boolean) => {
    const form = new FormData();
    append(form, "PersonalData.Bvn", values.bvn);
    append(form, "PersonalData.Nin", values.nin);
    append(form, "PersonalData.Title", values.title);
    append(form, "PersonalData.Surname", values.surname);
    append(form, "PersonalData.FirstName", values.firstName);
    append(form, "PersonalData.MiddleName", values.middleName);
    append(form, "PersonalData.MaidenOrFormerName", values.maidenOrFormerName);
    append(form, "PersonalData.MaritalStatusId", values.maritalStatusId);
    append(form, "PersonalData.Nationality", normalizeNationality(values.nationality));
    append(form, "PersonalData.LGAOfOriginCodeId", values.lgaOfOriginCodeId);
    append(form, "PersonalData.DateOfBirth", values.dateOfBirth);
    append(form, "PersonalData.PlaceOfBirth", values.placeOfBirth);
    append(form, "PersonalData.Email", values.email);
    append(form, "PersonalData.PhoneNumber", values.phoneNumber);
    append(form, "PersonalData.ResidenceCountryCode", values.residenceCountryCode);
    append(form, "PersonalData.ResidenceStateCode", values.residenceStateCode);
    append(form, "PersonalData.ResidenceLocalGovernmentId", values.residenceLocalGovernmentId);
    append(form, "PersonalData.ResidenceTownCity", values.residenceTownCity);
    append(form, "PersonalData.ResidenceStreetName", values.residenceStreetName);
    append(form, "PersonalData.ResidenceHouseNameOrNumber", values.residenceHouseNameOrNumber);
    append(form, "PersonalData.ResidenceZipCode", values.residenceZipCode);

    append(form, "EmploymentRecord.EmployerBranchId", values.employerBranchId || 0);
    append(form, "EmploymentRecord.StateCode", values.employmentStateCode);
    append(form, "EmploymentRecord.LocalGovernmentId", values.employmentLocalGovernmentId);
    append(form, "EmploymentRecord.TownCity", values.employmentTownCity);
    append(form, "EmploymentRecord.StreetName", values.employmentStreetName);
    append(form, "EmploymentRecord.BuildingNameOrNumber", values.employmentBuildingNameOrNumber);
    append(form, "EmploymentRecord.NatureOfBusiness", values.natureOfBusiness);
    append(form, "EmploymentRecord.DateOfFirstAppointment", values.dateOfFirstAppointment);
    append(form, "EmploymentRecord.DateOfCurrentEmployment", values.dateOfCurrentEmployment);

    append(form, "NextOfKinDetail.Title", values.nokTitle);
    append(form, "NextOfKinDetail.Gender", values.nokGender);
    append(form, "NextOfKinDetail.Surname", values.nokSurname);
    append(form, "NextOfKinDetail.FirstName", values.nokFirstName);
    append(form, "NextOfKinDetail.MiddleName", values.nokMiddleName);
    append(form, "NextOfKinDetail.RelationshipId", values.relationshipId);
    append(form, "NextOfKinDetail.ResidenceCountryId", values.nokResidenceCountryId);
    append(form, "NextOfKinDetail.ResidenceStateCode", values.nokResidenceStateCode);
    append(form, "NextOfKinDetail.ResidenceTownCity", values.nokResidenceTownCity);
    append(form, "NextOfKinDetail.ResidenceStreetName", values.nokResidenceStreetName);
    append(form, "NextOfKinDetail.ResidenceHouseNumber", values.nokResidenceHouseNumber);
    append(form, "NextOfKinDetail.Email", values.nokEmail);
    append(form, "NextOfKinDetail.PhoneNumber", values.nokPhoneNumber);

    if (includeDocs) {
      // Indices must be contiguous from 0 or ASP.NET form binding drops later files.
      let docIndex = 0;
      if (pictureFile) {
        form.append(`RecaptureDocuments[${docIndex}].DocumentType`, "1");
        form.append(`RecaptureDocuments[${docIndex}].File`, pictureFile);
        docIndex += 1;
      }
      if (signatureFile) {
        form.append(`RecaptureDocuments[${docIndex}].DocumentType`, "2");
        form.append(`RecaptureDocuments[${docIndex}].File`, signatureFile);
        docIndex += 1;
      }
      if (consentFile) {
        form.append(`RecaptureDocuments[${docIndex}].DocumentType`, "3");
        form.append(`RecaptureDocuments[${docIndex}].File`, consentFile);
        docIndex += 1;
      }
    }

    return form;
  };

  const apiErrorMessage = (err: any, fallback: string) => {
    const data = err?.response?.data;
    if (!data) return fallback;
    if (data.statusMessage) return data.statusMessage;
    if (data.errors) {
      const msgs = Object.values(data.errors)
        .flat()
        .filter(Boolean)
        .map(String);
      if (msgs.length) return msgs.join(" ");
    }
    if (data.title && data.title !== "One or more validation errors occurred.") {
      return data.title;
    }
    if (data.title && data.errors) {
      const msgs = Object.values(data.errors).flat().map(String);
      return msgs.length ? msgs.join(" ") : data.title;
    }
    return data.title || fallback;
  };

  const onSubmit = async (values: any) => {
    if (!canEdit) {
      toast.info(statusMessage || "Recapture cannot be submitted.");
      return;
    }
    if (!pictureFile && !hasExistingPicture) {
      toast.error("Please upload your passport photograph.");
      return;
    }
    if (pictureFile) {
      if (faceValidating) {
        toast.info("Please wait for passport face validation to finish.");
        return;
      }
      if (faceCompliant !== true) {
        toast.error(faceStatusMessage || "Passport photograph must pass face validation before submit.");
        return;
      }
    }
    if (!signatureFile && !hasExistingSignature) {
      toast.error("Please upload your signature.");
      return;
    }
    if (!consentFile && !hasExistingConsent) {
      toast.error("Please upload the consent form.");
      return;
    }

    const form = buildFormData(values, true);
    form.append("SaveAsDraft", "false");

    setSubmitting(true);
    try {
      const response = await selfServicesService.submitRecapture(form);
      if (response?.data?.statusCode === "00" || response?.data?.successful) {
        setShowSuccess(true);
        setCanEdit(false);
        setStatusMessage(response.data.statusMessage || "Submitted for approval.");
        setTimeout(() => navigate(RouteNames.RECAPTURE_STATUS), 1200);
      } else {
        toast.error(response?.data?.statusMessage || "Submission failed.");
      }
    } catch (err: any) {
      toast.error(apiErrorMessage(err, "Unable to submit recapture."));
    } finally {
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    if (!canEdit) {
      toast.info(statusMessage || "Recapture cannot be edited.");
      return;
    }
    const values = getValues();
    try {
      await draftValidationSchema.validate(values, { abortEarly: false });
    } catch (err: any) {
      const msg = err?.errors?.[0] || "Please fill required draft fields.";
      toast.error(msg);
      await trigger(["surname", "firstName", "employerBranchId"]);
      return;
    }
    if (!values.employerBranchId) {
      toast.error("Please select an employer and branch before saving a draft.");
      return;
    }

    const form = buildFormData(values, true);
    form.set("SaveAsDraft", "true");

    setSavingDraft(true);
    try {
      const response = await selfServicesService.saveRecaptureDraft(form);
      if (response?.data?.statusCode === "00" || response?.data?.successful) {
        setIsDraft(true);
        setStatusDisplay("Draft");
        setStatusMessage(response.data.statusMessage || "Draft saved.");
        if (signatureFile) setHasExistingSignature(true);
        if (consentFile) setHasExistingConsent(true);
        if (pictureFile) setHasExistingPicture(true);
        // Clear File objects so next save doesn't re-upload; keep data-URL/blob previews.
        setPictureFile(null);
        setSignatureFile(null);
        setConsentFile(null);
        // Reload from API so signature/consent previews come from draft snapshots.
        try {
          const details = await selfServicesService.getRecaptureDetails();
          const data = details?.data?.responseObject;
          if (data) {
            setHasExistingPicture(!!data.hasPicture);
            setHasExistingSignature(!!data.hasSignature);
            setHasExistingConsent(!!data.hasConsentForm);
            if (data.picturePreview) {
              setPicturePreview(
                data.picturePreview.startsWith("data:")
                  ? data.picturePreview
                  : `data:image/jpeg;base64,${data.picturePreview}`
              );
            }
            if (data.signaturePreview) {
              setSignaturePreview(
                data.signaturePreview.startsWith("data:")
                  ? data.signaturePreview
                  : `data:image/jpeg;base64,${data.signaturePreview}`
              );
            }
            if (data.consentPreview) {
              const isPdf = !!data.consentIsPdf;
              setConsentIsPdf(isPdf);
              setConsentPreview(
                data.consentPreview.startsWith("data:")
                  ? data.consentPreview
                  : isPdf
                    ? `data:application/pdf;base64,${data.consentPreview}`
                    : `data:image/jpeg;base64,${data.consentPreview}`
              );
            }
          }
        } catch {
          /* keep local previews if refresh fails */
        }
        toast.success(response.data.statusMessage || "Draft saved successfully.");
      } else {
        toast.error(response?.data?.statusMessage || "Unable to save draft.");
      }
    } catch (err: any) {
      toast.error(apiErrorMessage(err, "Unable to save draft."));
    } finally {
      setSavingDraft(false);
    }
  };

  if (user?.isRecaptured) {
    return <Navigate to={RouteNames.HOME} replace />;
  }

  return (
    <div className="transaction-page settings-page">
      <PageName pageName="Recapture" />
      <SuccessModal
        showModal={showSuccess}
        onClose={() => {
          setShowSuccess(false);
          navigate(RouteNames.RECAPTURE_STATUS);
        }}
        note="Your recapture has been submitted and is awaiting approval."
      />
      <Card className="transaction-card">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b pb-4 mb-4">
          <div className="h1 text-grayscaleBody">RSA Data Recapture</div>
          <Link to={RouteNames.RECAPTURE_STATUS} className="btn btn-outline text-sm">
            View status
          </Link>
        </div>
        {(statusMessage || statusDisplay) && (
          <div className="mb-4 text-sm text-grayscaleBody bg-slate-50 border border-slate-100 rounded-xl px-4 py-3">
            {statusDisplay && <strong className="block mb-1">{statusDisplay}</strong>}
            {statusMessage}
            {isDraft && (
              <span className="block mt-1 text-xs text-slate-500">
                Draft is saved. Continue editing, then submit for approval when ready.
              </span>
            )}
          </div>
        )}
        {loading ? (
          <LoadingSpinner size="md" />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-8">
            <fieldset disabled={!canEdit} className="space-y-8 min-w-0">
            <section>
              <div className="form__head mb-4">Personal information</div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Title *</label>
                  <select className="form__select" {...register("title", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {TITLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {errors.title && <ErrorViewer message={errors.title.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">Marital status *</label>
                  <select className="form__select" {...register("maritalStatusId", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {MARITAL_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {errors.maritalStatusId && <ErrorViewer message={errors.maritalStatusId.message as string} />}
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Surname *</label>
                  <input className="form__input" {...register("surname")} />
                  {errors.surname && <ErrorViewer message={errors.surname.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">First name *</label>
                  <input className="form__input" {...register("firstName")} />
                  {errors.firstName && <ErrorViewer message={errors.firstName.message as string} />}
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Middle name</label>
                  <input className="form__input" {...register("middleName")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Maiden / former name</label>
                  <input className="form__input" {...register("maidenOrFormerName")} />
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">BVN *</label>
                  <input className="form__input" {...register("bvn")} />
                  {errors.bvn && <ErrorViewer message={errors.bvn.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">NIN *</label>
                  <input className="form__input" {...register("nin")} />
                  {errors.nin && <ErrorViewer message={errors.nin.message as string} />}
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Date of birth *</label>
                  <input type="date" className="form__input" {...register("dateOfBirth")} />
                  {errors.dateOfBirth && <ErrorViewer message={errors.dateOfBirth.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">Place of birth *</label>
                  <input className="form__input" {...register("placeOfBirth")} />
                  {errors.placeOfBirth && <ErrorViewer message={errors.placeOfBirth.message as string} />}
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Email</label>
                  <input className="form__input" type="email" {...register("email")} />
                  {errors.email && <ErrorViewer message={errors.email.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">Phone number *</label>
                  <input className="form__input" {...register("phoneNumber")} />
                  {errors.phoneNumber && <ErrorViewer message={errors.phoneNumber.message as string} />}
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Nationality *</label>
                  <select className="form__select" {...register("nationality")}>
                    <option value="">Select</option>
                    {NATIONALITY_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                  {errors.nationality && <ErrorViewer message={errors.nationality.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">Residence country *</label>
                  <select className="form__select" {...register("residenceCountryCode")}>
                    <option value="">Select</option>
                    {(allCountries || []).map((c: any) => (
                      <option key={c.id || c.txtcountrycode} value={c.txtcountrycode || c.txtCountryCode}>
                        {c.txtcountryName || c.txtCountryName || c.txtcountrycode}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Residence state *</label>
                  <select className="form__select" {...register("residenceStateCode")}>
                    <option value="">Select</option>
                    {allStates && Object.keys(allStates).map((code) => (
                      <option key={code} value={code}>{allStates[code]}</option>
                    ))}
                  </select>
                  {errors.residenceStateCode && <ErrorViewer message={errors.residenceStateCode.message as string} />}
                </div>
                <div className="form__group">
                  <label className="form__label">Residence LGA *</label>
                  <select className="form__select" {...register("residenceLocalGovernmentId", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {lgas && Object.keys(lgas).map((id) => (
                      <option key={id} value={id}>{lgas[id]}</option>
                    ))}
                  </select>
                  {errors.residenceLocalGovernmentId && <ErrorViewer message={errors.residenceLocalGovernmentId.message as string} />}
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Town / City *</label>
                  <input className="form__input" {...register("residenceTownCity")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Street *</label>
                  <input className="form__input" {...register("residenceStreetName")} />
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">House number *</label>
                  <input className="form__input" {...register("residenceHouseNameOrNumber")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Zip code</label>
                  <input className="form__input" {...register("residenceZipCode")} />
                </div>
              </div>
            </section>

            <section>
              <div className="form__head mb-4">Employment</div>
              <div className="form__group mb-4">
                <label className="form__label">Employer *</label>
                <EmployerSelect onChange={onEmployerSelected} defaultValue={employerDefault} />
              </div>
              <div className="form__group mb-4">
                <label className="form__label">Employer branch *</label>
                <select className="form__select" {...register("employerBranchId", { valueAsNumber: true })}>
                  <option value="">Select branch</option>
                  {branchOptions.map((b) => (
                    <option key={b.value} value={b.value}>{b.label}</option>
                  ))}
                </select>
                {(branchLoading || !employerBranchId) && (
                  <p className="text-xs text-slate-500 mt-1">Search and select an employer to load branches.</p>
                )}
                {errors.employerBranchId && <ErrorViewer message={errors.employerBranchId.message as string} />}
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Nature of business</label>
                  <input className="form__input" {...register("natureOfBusiness")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Date of first appointment</label>
                  <input type="date" className="form__input" {...register("dateOfFirstAppointment")} />
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Date of current employment</label>
                  <input type="date" className="form__input" {...register("dateOfCurrentEmployment")} />
                </div>
              </div>
            </section>

            <section>
              <div className="form__head mb-4">Next of kin</div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Title *</label>
                  <select className="form__select" {...register("nokTitle", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {TITLE_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div className="form__group">
                  <label className="form__label">Gender *</label>
                  <select className="form__select" {...register("nokGender", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {GENDER_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Surname *</label>
                  <input className="form__input" {...register("nokSurname")} />
                </div>
                <div className="form__group">
                  <label className="form__label">First name *</label>
                  <input className="form__input" {...register("nokFirstName")} />
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Middle name</label>
                  <input className="form__input" {...register("nokMiddleName")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Relationship *</label>
                  <select className="form__select" {...register("relationshipId", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {(relationships || []).map((r: any) => (
                      <option key={r.id} value={r.id}>
                        {r.txtRelationshipType || r.txtrelationship || r.name}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Phone *</label>
                  <input className="form__input" {...register("nokPhoneNumber")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Email</label>
                  <input className="form__input" {...register("nokEmail")} />
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Country *</label>
                  <select className="form__select" {...register("nokResidenceCountryId", { valueAsNumber: true })}>
                    <option value="">Select</option>
                    {(allCountries || []).map((c: any) => (
                      <option key={c.id} value={c.id}>
                        {c.txtcountryName || c.txtCountryName}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form__group">
                  <label className="form__label">State</label>
                  <input className="form__input" {...register("nokResidenceStateCode")} />
                </div>
              </div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Town / City</label>
                  <input className="form__input" {...register("nokResidenceTownCity")} />
                </div>
                <div className="form__group">
                  <label className="form__label">Street</label>
                  <input className="form__input" {...register("nokResidenceStreetName")} />
                </div>
              </div>
              <div className="form__group">
                <label className="form__label">House number</label>
                <input className="form__input" {...register("nokResidenceHouseNumber")} />
              </div>
            </section>

            <section>
              <div className="form__head mb-4">Photo & documents *</div>
              <div className="form__row">
                <div className="form__group">
                  <label className="form__label">Passport photograph (jpg/png) *</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    className="form__input"
                    onChange={(e) => onPictureSelected(e.target.files?.[0] || null)}
                  />
                  {picturePreview && (
                    <div className="mt-3">
                      <img
                        src={
                          picturePreview.startsWith("data:") || picturePreview.startsWith("blob:")
                            ? picturePreview
                            : `data:image/jpeg;base64,${picturePreview}`
                        }
                        alt="Passport preview"
                        className="h-28 w-28 object-cover rounded-xl border"
                      />
                    </div>
                  )}
                  {faceValidating && (
                    <p className="text-xs text-slate-500 mt-2">Checking passport with Aware face validation…</p>
                  )}
                  {!faceValidating && faceStatusMessage && (
                    <div
                      className={`mt-2 text-sm rounded-xl px-3 py-2 border ${
                        faceCompliant
                          ? "bg-green-50 border-green-100 text-green-800"
                          : "bg-red-50 border-red-100 text-red-800"
                      }`}
                    >
                      {faceStatusMessage}
                    </div>
                  )}
                  {hasExistingPicture && !pictureFile && (
                    <p className="text-xs text-slate-500 mt-1">
                      Existing photo on file. Upload a new one before final submit.
                    </p>
                  )}
                </div>
                <div className="form__group">
                  <label className="form__label">Signature (jpg/png) *</label>
                  <input
                    type="file"
                    accept="image/jpeg,image/png,image/jpg"
                    className="form__input"
                    onChange={(e) => onSignatureSelected(e.target.files?.[0] || null)}
                  />
                  {signaturePreview && (
                    <div className="mt-3">
                      <img
                        src={
                          signaturePreview.startsWith("data:") || signaturePreview.startsWith("blob:")
                            ? signaturePreview
                            : `data:image/jpeg;base64,${signaturePreview}`
                        }
                        alt="Signature preview"
                        className="h-20 max-w-full object-contain rounded-xl border bg-white p-2"
                      />
                    </div>
                  )}
                  {hasExistingSignature && !signatureFile && (
                    <p className="text-xs text-slate-500 mt-1">Existing signature on file.</p>
                  )}
                </div>
              </div>
              <div className="form__group">
                <label className="form__label">Consent form (pdf/jpg/png) *</label>
                <input
                  type="file"
                  accept="image/jpeg,image/png,image/jpg,application/pdf"
                  className="form__input"
                  onChange={(e) => onConsentSelected(e.target.files?.[0] || null)}
                />
                {consentPreview && (
                  <div className="mt-3">
                    {consentIsPdf ? (
                      <a
                        href={
                          consentPreview.startsWith("data:") || consentPreview.startsWith("blob:")
                            ? consentPreview
                            : `data:application/pdf;base64,${consentPreview}`
                        }
                        target="_blank"
                        rel="noreferrer"
                        className="text-sm text-primary underline"
                      >
                        Preview consent PDF{consentFile?.name ? ` (${consentFile.name})` : ""}
                      </a>
                    ) : (
                      <img
                        src={
                          consentPreview.startsWith("data:") || consentPreview.startsWith("blob:")
                            ? consentPreview
                            : `data:image/jpeg;base64,${consentPreview}`
                        }
                        alt="Consent preview"
                        className="h-28 max-w-full object-contain rounded-xl border bg-white p-2"
                      />
                    )}
                  </div>
                )}
                {hasExistingConsent && !consentFile && (
                  <p className="text-xs text-slate-500 mt-1">Existing consent form on file.</p>
                )}
              </div>
              <p className="text-xs text-slate-500 mt-2">
                Documents are optional when saving a draft. Fresh photo, signature and consent are required to submit for approval. Passport must pass Aware face validation before submit.
              </p>
            </section>
            </fieldset>

            <div className="flex flex-wrap justify-end gap-3 pt-2">
              <button
                type="button"
                className="btn btn-outline"
                disabled={savingDraft || submitting || !canEdit}
                onClick={onSaveDraft}
              >
                {savingDraft ? "Saving…" : "Save as draft"}
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={
                  submitting ||
                  savingDraft ||
                  !canEdit ||
                  faceValidating ||
                  (pictureFile != null && faceCompliant !== true)
                }
              >
                {submitting ? "Submitting…" : "Submit for approval"}
              </button>
            </div>
          </form>
        )}
      </Card>
    </div>
  );
};

export default InactivityTimeoutHOC(RecapturePage, 900000);
